import React, { useMemo, useState } from "react";
import { Card } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import TeamLogo from "@/components/TeamLogo";
import AdSlot from "@/components/AdSlot";
import { AD_SLOTS, AD_INFEED_LAYOUT_KEY, hasAd } from "@/lib/ads";
import ProbabilityDetail from "@/components/ProbabilityDetail";
import { Sheet, SheetContent } from "@/components/ui/sheet";
import { Sparkles, Lock, AlertTriangle } from "lucide-react";

const tier = (p) => {
  if (p >= 85) return { label: "Likely", color: "bg-primary/20 text-primary", cls: "progress-good" };
  if (p >= 50) return { label: "On track", color: "bg-primary/10 text-primary", cls: "progress-good" };
  if (p >= 15) return { label: "Live", color: "bg-amber-500/15 text-amber-400", cls: "progress-warn" };
  if (p > 0)   return { label: "Long shot", color: "bg-destructive/15 text-destructive", cls: "progress-bad" };
  return       { label: "Eliminated", color: "bg-destructive/25 text-destructive", cls: "progress-bad" };
};

const ProbabilitiesTab = ({ probabilities, standings, fixtures, teamMap }) => {
  const [openTeam, setOpenTeam] = useState(null);

  const sorted = useMemo(() => {
    const entries = Object.entries(probabilities.probabilities);
    entries.sort((a, b) => b[1] - a[1]);
    return entries;
  }, [probabilities]);

  const fixturesById = useMemo(() => {
    const m = {};
    for (const f of fixtures.all) m[f.id] = f;
    return m;
  }, [fixtures]);

  const standingsByShort = useMemo(() => {
    const m = {};
    for (const r of standings.rows) m[r.short] = r;
    return m;
  }, [standings]);

  return (
    <div className="space-y-3" data-testid="probabilities-tab">
      <div className="flex items-baseline justify-between">
        <h2 className="font-display uppercase tracking-tight text-xl">Top-4 Probability</h2>
        <span className="text-[10px] uppercase tracking-widest text-muted-foreground">
          Tap a team for paths & what-ifs
        </span>
      </div>

      <Card className="bg-card border-border rounded-md p-3">
        <div className="flex items-center gap-2 text-[11px] text-muted-foreground">
          <Sparkles className="w-3.5 h-3.5 text-primary" />
          {probabilities.method === "brute_force" ? (
            <span>
              Exact enumeration over <span className="num text-foreground">{probabilities.total_scenarios.toLocaleString()}</span> scenarios
              ({probabilities.pending_count} matches remaining).
            </span>
          ) : probabilities.method === "final" ? (
            <span>League stage complete — final positions locked.</span>
          ) : (
            <span>
              Monte-Carlo over <span className="num text-foreground">{probabilities.total_scenarios.toLocaleString()}</span> simulations
              ({probabilities.pending_count} matches remaining, ±{(100 / Math.sqrt(probabilities.total_scenarios)).toFixed(2)}% margin).
            </span>
          )}
        </div>
      </Card>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="probability-grid">
        {sorted.map(([short, pct], idx) => (
          <React.Fragment key={short}>
            <ProbabilityCard
              short={short}
              pct={pct}
              team={teamMap[short]}
              row={standingsByShort[short]}
              onOpen={() => setOpenTeam(short)}
            />
            {idx === 3 && hasAd("infeed") && (
              <div className="sm:col-span-2" data-testid="ad-infeed-wrap">
                <AdSlot
                  slot={AD_SLOTS.infeed}
                  format={AD_INFEED_LAYOUT_KEY ? "fluid" : "auto"}
                  layout={AD_INFEED_LAYOUT_KEY ? "in-feed" : undefined}
                  layoutKey={AD_INFEED_LAYOUT_KEY || undefined}
                  responsive
                  testId="ad-infeed"
                  className="h-[100px] sm:h-[140px] w-full"
                />
              </div>
            )}
          </React.Fragment>
        ))}
      </div>

      <Sheet
        open={!!openTeam}
        onOpenChange={(v) => !v && setOpenTeam(null)}
      >
        <SheetContent
          side="bottom"
          className="bg-background border-t border-border h-[85vh] sm:h-[80vh] overflow-y-auto p-0"
          data-testid="probability-sheet"
        >
          {openTeam && (
            <ProbabilityDetail
              short={openTeam}
              team={teamMap[openTeam]}
              probability={probabilities.probabilities[openTeam]}
              samplePaths={probabilities.sample_paths[openTeam] || []}
              whatIf={probabilities.what_if}
              fixturesById={fixturesById}
              teamMap={teamMap}
              standingsByShort={standingsByShort}
              method={probabilities.method}
              totalScenarios={probabilities.total_scenarios}
            />
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
};

const ProbabilityCard = ({ short, pct, team, row, onOpen }) => {
  const t = tier(pct);
  return (
    <Card
      role="button"
      tabIndex={0}
      onClick={onOpen}
      onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && onOpen()}
      className="bg-card border-border rounded-md p-4 cursor-pointer hover:border-primary/50 transition-colors duration-150"
      data-testid={`prob-card-${short}`}
    >
      <div className="flex items-start gap-3">
        <TeamLogo team={team} size={40} />
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-2">
            <div className="font-display uppercase text-base leading-tight truncate">
              {team?.short}
            </div>
            <span
              className={`text-[9px] uppercase tracking-widest px-1.5 py-0.5 rounded-sm ${t.color}`}
            >
              {pct === 0 && <Lock className="inline w-2.5 h-2.5 mr-0.5" />}
              {t.label}
            </span>
          </div>
          <div className="text-[11px] text-muted-foreground truncate">{team?.name}</div>
        </div>
      </div>

      <div className="mt-3 flex items-end justify-between">
        <div>
          <div className="num text-5xl leading-none text-foreground">
            {pct.toFixed(pct === 0 || pct === 100 ? 0 : 1)}
            <span className="text-xl text-muted-foreground">%</span>
          </div>
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground mt-1">
            qualify chance
          </div>
        </div>
        {row && (
          <div className="text-right">
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Now</div>
            <div className="num text-sm">
              {row.Pts} <span className="text-muted-foreground">pts</span>
            </div>
            <div className="num text-xs text-muted-foreground">NRR {row.NRR.toFixed(3)}</div>
          </div>
        )}
      </div>

      <Progress value={pct} className={`h-1.5 mt-3 bg-secondary ${t.cls}`} />

      {pct === 0 && (
        <div className="mt-2 inline-flex items-center gap-1 text-[10px] text-destructive">
          <AlertTriangle className="w-3 h-3" /> Mathematically eliminated
        </div>
      )}
    </Card>
  );
};

export default ProbabilitiesTab;
